import React, { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchJson } from '../../../helpers/fetch.js';
import { gridTheme, gridThemeNoBorder } from '../../common/AgGrid/gridTheme.js';
import { useHierarchyBuilder } from './useHierarchyBuilder.js';
import withSuspense from '../../../helpers/withSuspense.jsx';
import InputSkeleton from '../../../skeleton_loaders/common/InputSkeleton.jsx';
import SkeletonGrid from '../../../skeleton_loaders/common/SkeletonGrid.jsx';

const GRID_HEIGHT = 'calc(100vh - 400px)';

const AgGridReact = withSuspense(lazy(() => import('../../common/AgGrid/AgGridReact.jsx')), <SkeletonGrid gridHeight={GRID_HEIGHT} />);
const Select = withSuspense(lazy(() => import('../../common/Select.jsx')), <InputSkeleton hideLabel={true} />)
const HierarchySelect = withSuspense(lazy(() => import('./HierarchySelect.jsx')), <InputSkeleton hideLabel={true} />)

import {
  option_set_list_json_path
} from '~/routes';

function HierarchyBuilder({ projectId, hierarchy }) {
  const {
    isSaving,
    rowData,
    selectedOptionSetIds,
    handleOptionSetsChange,
    handleRowDragEnd,
    isRowValidDropPosition
  } = useHierarchyBuilder({ hierarchyId: hierarchy.id });

  const { data: { option_sets: optionSets = [] } = {}, } = useQuery({
    queryKey: ['optionSets', projectId],
    queryFn: () => fetchJson(option_set_list_json_path({ project_id: projectId })),
  });

  const autoGroupColumnDef = useMemo(() => ({
    field: 'name',
    headerName: I18n.t('option'),
    rowDrag: true,
    flex: 1,
  }), []);

  const rowSelection = useMemo(() => ({ mode: 'multiRow' }), []);

  const getRowId = useCallback((params) => String(params.data.id), []);

  const optionSetMap = useMemo(() => {
    return optionSets.reduce((acc, optionSet) => {
      acc[optionSet.id] = optionSet;
      return acc;
    }, {});
  }, [optionSets])

  return (
    <div>
      <h4>{hierarchy.name}</h4>
      <div className='mb-1 pr-1'>
        {/* <div className='mb-1'>
          <label>{I18n.t('preview')}</label>
          <HierarchySelect
            name='option_key'
            id='option_key'
            placeholder={I18n.t('select')}
            hierarchyKey={hierarchy.key}
            isClearable
            isMulti
          />
        </div> */}
        <div>
          <label>{I18n.t('option_sets')}</label>
          <Select
            options={optionSets}
            value={selectedOptionSetIds}
            onChange={handleOptionSetsChange}
            isMulti
          />
        </div>
      </div>
      <div className='grid-theme' style={{ height: GRID_HEIGHT }}>
        <AgGridReact
          theme={gridThemeNoBorder}
          rowData={rowData}
          treeData
          treeDataParentIdField='parentId'
          getRowId={getRowId}
          autoGroupColumnDef={autoGroupColumnDef}
          rowSelection={rowSelection}
          columnDefs={[{
            field: 'option_set',
            headerName: I18n.t('option_set'),
            rowDrag: false,
            valueGetter: params => {
              return (optionSetMap || {})[params.data.optionSetId]?.name || '';
            },
          }]}
          groupDefaultExpanded={-1}
          suppressMoveWhenRowDragging
          onRowDragEnd={handleRowDragEnd}
          rowDragManaged={true}
          rowDragMultiRow={true}
          isRowValidDropPosition={isRowValidDropPosition}
        />
      </div>
    </div>
  );
}

export default HierarchyBuilder;
